import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
  faInbox,
  faSort,
  faSortUp,
  faSortDown
} from '@fortawesome/free-solid-svg-icons';

// Column definition type
export interface Column<T> {
  key: string;
  header: string | React.ReactNode;
  width?: string;
  sortable?: boolean;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

// Pagination settings
export interface PaginationConfig {
  enabled?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showInfo?: boolean;
  showFirstLast?: boolean;
}

// Props for DataTable
export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  keyExtractor?: (item: T, index: number) => string | number;
  onRowClick?: (item: T, index: number) => void;
  rowClassName?: string | ((item: T, index: number) => string);
  striped?: boolean;
  hoverable?: boolean;
  bordered?: boolean;
  compact?: boolean;
  
  // Pagination props
  pagination?: PaginationConfig | boolean;
  
  // Server-side pagination (when data comes from server)
  serverPagination?: {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    pageSizeOptions?: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
  };
  
  // Footer
  footer?: React.ReactNode;
  
  // Custom header content
  headerContent?: React.ReactNode;
  
  // Table container className
  className?: string;
}

// Pagination component
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  pageSizeOptions: number[];
  showPageSizeSelector: boolean;
  showInfo: boolean;
  showFirstLast: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  pageSizeOptions,
  showPageSizeSelector,
  showInfo,
  showFirstLast,
  onPageChange,
  onPageSizeChange
}) => {
  const { t } = useTranslation();
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('ellipsis');
      }
      
      // Pages around current
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  if (totalPages <= 1 && !showInfo) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 bg-gray-50/50 dark:bg-slate-700/30 border-t border-gray-100 dark:border-slate-700">
      {/* Left side - Info and page size */}
      <div className="flex items-center gap-4">
        {showInfo && (
          <span className="text-sm text-gray-600 dark:text-slate-400">
            {t('dataTable.showing')} <span className="font-semibold text-gray-800 dark:text-slate-200">{startItem}-{endItem}</span> {t('dataTable.of')}{' '}
            <span className="font-semibold text-gray-800 dark:text-slate-200">{totalItems.toLocaleString()}</span> {t('dataTable.entries')}
          </span>
        )}

        {showPageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-slate-400">{t('dataTable.show')}</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white cursor-pointer"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500 dark:text-slate-400">{t('dataTable.entries')}</span>
          </div>
        )}
      </div>

      {/* Right side - Pagination buttons */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* First Page */}
          {showFirstLast && (
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              title={t('dataTable.firstPage')}
            >
              <FontAwesomeIcon icon={faAnglesLeft} className="text-sm" />
            </button>
          )}

          {/* Previous Page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
            title={t('dataTable.previousPage')}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1 mx-1">
            {getPageNumbers().map((page, index) => (
              page === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="w-9 h-9 flex items-center justify-center text-gray-400 dark:text-slate-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg font-medium text-sm transition-all ${
                    currentPage === page
                      ? 'text-white shadow-md'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                  style={currentPage === page ? { background: 'var(--theme-primary-gradient)' } : undefined}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Next Page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
            title={t('dataTable.nextPage')}
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-sm" />
          </button>

          {/* Last Page */}
          {showFirstLast && (
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              title={t('dataTable.lastPage')}
            >
              <FontAwesomeIcon icon={faAnglesRight} className="text-sm" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Loading skeleton
const TableSkeleton: React.FC<{ columns: number; rows?: number }> = ({ columns, rows = 5 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={rowIndex} className="animate-pulse">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <td key={colIndex} className="px-4 py-4">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// Main DataTable component
function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage,
  emptyIcon,
  keyExtractor,
  onRowClick,
  rowClassName,
  striped = false,
  hoverable = true,
  bordered = false,
  compact = false,
  pagination = false,
  serverPagination,
  footer,
  headerContent,
  className = ''
}: DataTableProps<T>) {
  const { t } = useTranslation();

  // Client-side pagination state
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(
    typeof pagination === 'object' ? (pagination.pageSize || 10) : 10
  );
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Determine pagination config
  const paginationConfig: PaginationConfig = useMemo(() => {
    if (serverPagination) {
      return {
        enabled: true,
        pageSize: serverPagination.pageSize,
        pageSizeOptions: serverPagination.pageSizeOptions ?? [10, 20, 50, 100],
        showPageSizeSelector: true,
        showInfo: true,
        showFirstLast: true
      };
    }
    
    if (typeof pagination === 'boolean') {
      return {
        enabled: pagination,
        pageSize: 10,
        pageSizeOptions: [10, 20, 50, 100],
        showPageSizeSelector: true,
        showInfo: true,
        showFirstLast: true
      };
    }
    
    return {
      enabled: pagination.enabled ?? true,
      pageSize: pagination.pageSize ?? 10,
      pageSizeOptions: pagination.pageSizeOptions ?? [10, 20, 50, 100],
      showPageSizeSelector: pagination.showPageSizeSelector ?? true,
      showInfo: pagination.showInfo ?? true,
      showFirstLast: pagination.showFirstLast ?? true
    };
  }, [pagination, serverPagination]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    
    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue, 'th')
          : bValue.localeCompare(aValue, 'th');
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [data, sortConfig]);

  // Client-side paginated data
  const paginatedData = useMemo(() => {
    if (serverPagination || !paginationConfig.enabled) {
      return sortedData;
    }
    
    const startIndex = (clientPage - 1) * clientPageSize;
    return sortedData.slice(startIndex, startIndex + clientPageSize);
  }, [sortedData, clientPage, clientPageSize, serverPagination, paginationConfig.enabled]);

  // Calculate pagination values
  const currentPage = serverPagination ? serverPagination.currentPage : clientPage;
  const pageSize = serverPagination ? serverPagination.pageSize : clientPageSize;
  const totalItems = serverPagination ? serverPagination.totalItems : data.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (serverPagination) {
      serverPagination.onPageChange(page);
    } else {
      setClientPage(page);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    if (serverPagination?.onPageSizeChange) {
      serverPagination.onPageSizeChange(newPageSize);
    } else {
      setClientPageSize(newPageSize);
      setClientPage(1); // Reset to first page
    }
  };

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.direction === 'asc' 
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
    
    // Reset to first page when sorting
    if (!serverPagination) {
      setClientPage(1);
    }
  };

  // Get sort icon
  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) {
      return <FontAwesomeIcon icon={faSort} className="text-gray-300 dark:text-slate-600 ml-1.5" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="text-primary ml-1.5" />
      : <FontAwesomeIcon icon={faSortDown} className="text-primary ml-1.5" />;
  };

  // Get row class
  const getRowClassName = (item: T, index: number): string => {
    const baseClass = `${hoverable ? 'hover:bg-gray-50 dark:hover:bg-slate-700/50' : ''} ${striped && index % 2 === 1 ? 'bg-gray-50/50 dark:bg-slate-700/30' : ''} transition-colors`;
    const cursorClass = onRowClick ? 'cursor-pointer' : '';
    
    if (typeof rowClassName === 'function') {
      return `${baseClass} ${cursorClass} ${rowClassName(item, index)}`;
    }
    return `${baseClass} ${cursorClass} ${rowClassName || ''}`;
  };

  // Get key for row
  const getRowKey = (item: T, index: number): string | number => {
    if (keyExtractor) {
      return keyExtractor(item, index);
    }
    return item.id || item._id || index;
  };

  const cellPadding = compact ? 'px-3 py-2.5' : 'px-4 py-4';

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden ${className}`}>
      {/* Optional header content */}
      {headerContent && (
        <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
          {headerContent}
        </div>
      )}

      {/* Table container */}
      <div className="overflow-x-auto">
        <table className={`min-w-full ${bordered ? 'border-collapse' : ''}`}>
          {/* Table Header */}
          <thead className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${cellPadding} text-left text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors' : ''
                  } ${column.headerClassName || ''}`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center">
                    {column.header}
                    {column.sortable && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          {/* Table Body */}
          {loading ? (
            <TableSkeleton columns={columns.length} />
          ) : paginatedData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-3">
                    {emptyIcon || (
                      <FontAwesomeIcon icon={faInbox} className="text-5xl text-gray-300 dark:text-slate-600" />
                    )}
                    <p className="text-gray-500 dark:text-slate-400 text-lg">{emptyMessage || t('dataTable.noData')}</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {paginatedData.map((item, index) => (
                <tr
                  key={getRowKey(item, index)}
                  className={getRowClassName(item, index)}
                  onClick={() => onRowClick?.(item, index)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`${cellPadding} ${column.className || ''}`}
                    >
                      {column.render 
                        ? column.render(item, index)
                        : item[column.key] ?? '-'
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
          
          {/* Optional Footer */}
          {footer && !loading && paginatedData.length > 0 && (
            <tfoot className="bg-gray-50 dark:bg-slate-700/50 border-t-2 border-gray-200 dark:border-slate-700">
              {footer}
            </tfoot>
          )}
        </table>
      </div>
      
      {/* Pagination */}
      {paginationConfig.enabled && !loading && data.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={paginationConfig.pageSizeOptions || [10, 20, 50, 100]}
          showPageSizeSelector={paginationConfig.showPageSizeSelector ?? true}
          showInfo={paginationConfig.showInfo ?? true}
          showFirstLast={paginationConfig.showFirstLast ?? true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
}

export default DataTable;

